import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * Central Access-Mode source of truth for the whole Read-Only experience.
 *
 * Fetches ``/api/billing/status`` on mount + whenever the auth user changes
 * and exposes:
 *
 *   - ``mode``  — "active" | "grace" | "read_only"
 *   - ``isReadOnly`` — convenience boolean
 *   - ``isGrace``    — true during the 48 h post-payment-failure window
 *   - ``daysLeft``   — trial days remaining (0 if past trial or on paid plan)
 *   - ``gracePeriodUntil`` — ISO timestamp or null
 *   - ``refresh()``  — re-poll after checkout / cancel / etc.
 *
 * Founders always report ``active`` (the backend hard-codes that).
 */

const AccessCtx = createContext({
    mode: "active", isReadOnly: false, isGrace: false, loading: true,
    daysLeft: 0, gracePeriodUntil: null, status: "active", refresh: () => {},
});

export function AccessProvider({ children }) {
    const { user } = useAuth();
    const [state, setState] = useState({
        mode: "active", isReadOnly: false, isGrace: false, loading: true,
        daysLeft: 0, gracePeriodUntil: null, status: "active",
    });

    const refresh = useCallback(async () => {
        if (!user) {
            setState((s) => ({ ...s, mode: "active", isReadOnly: false, isGrace: false, loading: false }));
            return;
        }
        try {
            const { data } = await api.get("/billing/status");
            const mode = data.access_mode || (data.read_only ? "read_only" : "active");
            // Persist so BillingSuccess.jsx can differentiate a first-time
            // upgrade from a reactivation (Welcome-Back experience).
            try { localStorage.setItem("vu_last_access_mode", mode); } catch { /* ignore */ }
            setState({
                mode,
                isReadOnly: mode === "read_only",
                isGrace: mode === "grace",
                loading: false,
                daysLeft: data.days_left || 0,
                gracePeriodUntil: data.grace_period_until || null,
                status: data.subscription_status || "active",
            });
        } catch (e) {
            // Fail open — we never lock a user out because of a status-fetch hiccup.
            console.debug("[access] status fetch failed:", e?.message);
            setState((s) => ({ ...s, loading: false }));
        }
    }, [user]);

    useEffect(() => { refresh(); }, [refresh]);

    // Re-check when Stripe redirects back to /billing/success (which triggers
    // a global "vu:billing-refresh" event dispatched by BillingSuccess.jsx).
    useEffect(() => {
        const onRefresh = () => refresh();
        window.addEventListener("vu:billing-refresh", onRefresh);
        return () => window.removeEventListener("vu:billing-refresh", onRefresh);
    }, [refresh]);

    return <AccessCtx.Provider value={{ ...state, refresh }}>{children}</AccessCtx.Provider>;
}

export function useAccess() {
    return useContext(AccessCtx);
}
